import moment from "moment";
import { PickupStatus } from "../../app/data/common/route";
import { TagColor } from "../../app/data/common/tagColor";
import { PickupConsigneeModel, PickupDetailsModel, PickupsGroupModel, PickupShipperModel, PickupsResponseContentModel } from "../../app/data/pickup/models";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import { WeekDay, weekDays } from "../../ui-components/xgs-date/xgs-date/xgsDate";
import { makeId } from "../../hooks/utils";
import { ItemModel } from "../../app/data/common/models";

export const getPickupById = (pickups, id) => (
  pickups.reduce((result, item) => {
    if (result) return result
    if (item.pickups?.length > 0) {
      return getPickupById(item.pickups, id)
    }
    return item.pickup.pickupId === id ? item : null
  }, null)
);

export const getPickupsByIds = (pickups, ids) => (
  pickups.reduce((result, item) => {
    if (item.pickups?.length > 0) {
      return [...result, ...getPickupsByIds(item.pickups, ids)];
    }
    return ids.find(id => id === item.pickup.pickupId) ? [...result, item] : result
  }, [])
);

export const getPickupGroupStatus = (items: PickupsResponseContentModel[]) => {
  if (items.find(item => item.status === PickupStatus.MISSED)) return PickupStatus.MISSED;
  if (items.find(item => item.status === PickupStatus.COMPLETED)) return PickupStatus.COMPLETED;
  if (items.find(item => item.status === PickupStatus.UNASSIGNED)) return PickupStatus.UNASSIGNED;
  if (items.find(item => item.status === PickupStatus.ASSIGNED)) return PickupStatus.ASSIGNED;
  if (items.find(item => item.status === PickupStatus.PENDING_REVIEW)) return PickupStatus.PENDING_REVIEW;
  return PickupStatus.CANCELED;
}

export const getPickupGroupDate = (items: PickupsResponseContentModel[], ids: string[]) => {
  return getPickupById(items, ids[0])?.pickup.date;
}

export const isGroup = (item: PickupsResponseContentModel | PickupsGroupModel): item is PickupsGroupModel => ("pickups" in item && !!item.pickups);

export const isSubPickup = (item: PickupsResponseContentModel | PickupsGroupModel): item is PickupsResponseContentModel => !isGroup(item) && !item.pickup.single;

export const convertToSubPickup = (item: PickupsResponseContentModel) => ({ ...item, pickup: { ...item.pickup, single: false } });

export const getStatusColor = (status: PickupStatus | string) => {
  switch (status) {
    case PickupStatus.ASSIGNED:
      return TagColor.YELLOW;
    case PickupStatus.UNASSIGNED:
      return TagColor.ORANGE;
    case PickupStatus.COMPLETED:
      return TagColor.GREEN;
    case PickupStatus.MISSED:
      return TagColor.RED;
    case PickupStatus.PENDING_REVIEW:
      return TagColor.PURPLE;
    case "IN_HOUSE":
      return TagColor.PURPLE;
    case "INBOUND":
      return TagColor.BLUE;
    //in-house statuses
    case "City Delivery Loading Door":
      return TagColor.BLUE;
    case "Loading":
      return TagColor.YELLOW;
    case "Unloading":
      return TagColor.ORANGE;
    case "Staging":
      return TagColor.PURPLE;
    case "Storage Rack":
      return TagColor.GREEN;
    case "Overage Bay":
      return TagColor.RED;
    // inbound statuses
    case "LATE":
      return TagColor.RED;
    case "ALERT":
      return TagColor.RED;
    default:
      return TagColor.GREY
  }
}

export const getStatusesOptions = () => {
  const keys = Object.keys(PickupStatus)
  const statusesOptions: XGSSelectOption[] = [];
  keys.forEach((key, index) => {
    let label = key.toLowerCase().replace("_", " ");
    label = label.charAt(0).toUpperCase() + label.slice(1);
    statusesOptions.push({
      label: label,
      value: key.toString()
    })
  });
  return statusesOptions;
};

export const getCustomerPickupSourceLabel = (source: string) => {
  switch (source) {
    case "Customer":
    case "Customer API": return "BOL";
    case "Customer Return": return "Return";
    case "Employee": return "XGS";
    case "EDI":
    case "EDI Pickup": return "EDI";
    default: return "-"
  }
};

export const getPickupDetails = (pickupData: PickupsResponseContentModel): PickupDetailsModel | null => {
  if (!pickupData) return null;

  const documents = pickupData.pickupDocuments ? [...pickupData.pickupDocuments].sort((document1, document2) => (document1.type > document2.type ? 1 : -1)) : [];

  return {
    pickupId: pickupData.pickup.pickupId,
    pickupNumber: pickupData.pickup.pickupNumber,
    probillNumber: pickupData.pickupFullData.pickupRequest.probillNumber,
    pickupStatus: pickupData.pickup.pickupStatus,
    missed: pickupData.pickup.missed,
    pickupDate: pickupData.pickupFullData.pickupRequest.pickupDate,
    pickupReadyTime: pickupData.pickupFullData.pickupRequest.pickupReadyTime,
    pickupCloseTime: pickupData.pickupFullData.pickupRequest.pickupCloseTime,
    dateCreated: pickupData.pickupFullData.pickupRequest.dateCreated,
    userCreated: pickupData.pickupFullData.pickupRequest.userCreated,
    creatorType: pickupData.pickupFullData.pickupRequest.creatorType,
    userUpdated: pickupData.pickupFullData.pickupRequest.userUpdated,
    dateUpdated: pickupData.pickupFullData.pickupRequest.dateUpdated,
    bolNumber: pickupData.pickupFullData.pickupRequest.bolNumber,
    poNumber: pickupData.pickupFullData.pickupRequest.poNumber,
    additionalReference: pickupData.pickupFullData.pickupRequest.additionalReference,
    specialInstructions: pickupData.pickupFullData.pickupRequest.specialInstructions,
    manifestNumber: pickupData.pickupFullData.pickupRequest.manifestNumber,
    driverId: pickupData.pickupFullData.pickupRequest.driverId,
    driverName: pickupData.pickup.driverName,
    requester: pickupData.requester?.teamName,
    shipper: {
      accountNumber: pickupData.pickupFullData.pickupRequest.shipperAccountNumber,
      name: pickupData.pickupFullData.pickupRequest.shipperName,
      address: {
        city: pickupData.pickupFullData.pickupRequest.shipperCity,
        state: pickupData.pickupFullData.pickupRequest.shipperState,
        address1: pickupData.pickupFullData.pickupRequest.shipperAddress,
        postalCode: pickupData.pickupFullData.pickupRequest.shipperZip,
        additionalAddressLine: pickupData.pickupFullData.pickupRequest.shipperAddressAdditional,
      },
      firstName: pickupData.pickupFullData.pickupRequest.shipperFirstName,
      lastName: pickupData.pickupFullData.pickupRequest.shipperLastName,
      mobilePhone: pickupData.pickupFullData.pickupRequest.shipperMobilePhone,
      allowSms: pickupData.pickupFullData.pickupRequest.shipperAllowSms,
      email: pickupData.pickupFullData.pickupRequest.shipperEmail,
    },
    consignee: {
      name: pickupData.pickupFullData.pickupRequest.consigneeName,
      address: {
        city: pickupData.pickupFullData.pickupRequest.consigneeCity,
        state: pickupData.pickupFullData.pickupRequest.consigneeState,
        address1: pickupData.pickupFullData.pickupRequest.consigneeAddress1,
        postalCode: pickupData.pickupFullData.pickupRequest.consigneePostalCode,
        additionalAddressLine: pickupData.pickupFullData.pickupRequest.consigneeAddressAdditional,
      },
      phone: pickupData.pickupFullData.pickupRequest.consigneePhone,
      email: pickupData.pickupFullData.pickupRequest.consigneeEmail,
    },
    payor: {
      name: pickupData.pickupFullData.pickupRequest.payorName,
      address: {
        city: pickupData.pickupFullData.pickupRequest.payorCity,
        state: pickupData.pickupFullData.pickupRequest.payorState,
        address1: pickupData.pickupFullData.pickupRequest.payorAddress,
        postalCode: pickupData.pickupFullData.pickupRequest.payorZip,
      },
      phone: pickupData.pickupFullData.pickupRequest.payorPhone,
    },
    documents: documents,
    items: pickupData.itemsList,
  }
};

export const getDateClassName = (date: string, pickupDays?: weekDays | null, holidays?: string[]) => {
  const dateDayOfWeek = moment(date).format("dd").toLowerCase() as WeekDay;;

  if (pickupDays && pickupDays[dateDayOfWeek] === false) {
    return "xgs-pickup__date__non-service-day";
  };

  if (holidays && holidays.find((holiday) => (moment(date).isSame(holiday, "day")))) {
    return "xgs-pickup__date__non-service-day";
  }

  return "";
};

export const getNonServiceDateNotification = (date: string, pickupDays?: weekDays | null, shipperHolidays?: string[], xgsHolidays?: string[]) => {
  const dateDayOfWeek = moment(date, "MM/DD/YYYY").format("dd").toLowerCase() as WeekDay;

  if (pickupDays && pickupDays[dateDayOfWeek] === false) {
    return "Please note that you selected non-service day!";
  };

  if (shipperHolidays?.length && shipperHolidays.find((holiday) => (moment(date).isSame(holiday, "day")))) {
    return "Please note that you selected shipper's holiday!";
  };

  if (xgsHolidays?.length && xgsHolidays.find((holiday) => (moment(date).isSame(holiday, "day")))) {
    return "Please note that you selected XGS holiday!";
  };

  return "";
};

export const isLockedPickup = (pickupStatus: PickupStatus) => pickupStatus === PickupStatus.CANCELED || pickupStatus === PickupStatus.COMPLETED || pickupStatus === PickupStatus.PENDING_REVIEW;

export const getStatusName = (pickupStatus: PickupStatus) => pickupStatus.toLowerCase().replace("_", " ");

export const mapToPickupShipperModel = (pickup: PickupsResponseContentModel): PickupShipperModel => {
  const pickupRequest = pickup.pickupFullData.pickupRequest;

  return {
    teamId: pickup.requester?.teamId || "",
    teamName: pickup.requester?.teamName || "",
    bolNumber: pickupRequest.bolNumber || undefined,
    poNumber: pickupRequest.poNumber || undefined,
    additionalReference: pickupRequest.additionalReference || undefined,
    specialInstructions: pickupRequest.specialInstructions || undefined,
    pickupDate: pickupRequest.pickupDate,
    pickupReadyTime: pickupRequest.pickupReadyTime ? moment(pickupRequest.pickupReadyTime, "h:mm:ss").format("hh:mm a") : undefined,
    pickupCloseTime: pickupRequest.pickupCloseTime ? moment(pickupRequest.pickupCloseTime, "h:mm:ss").format("hh:mm a") : undefined,
    shipper: {
      id: makeId(5),
      accountNumber: pickupRequest.shipperAccountNumber,
      name: pickup.shipper.name,
      address: pickup.shipper.address,
      contacts: {
        firstName: pickupRequest.shipperFirstName || "",
        lastName: pickupRequest.shipperLastName || "",
        email: pickupRequest.shipperEmail || "",
        mobilePhone: pickupRequest.shipperMobilePhone || "",
        allowSms: !!pickupRequest.shipperAllowSms
      }
    }
  };
}

export const mapToPickupConsigneeModel = (pickup: PickupsResponseContentModel): PickupConsigneeModel => {
  const pickupRequest = pickup.pickupFullData.pickupRequest;

  return {
    name: pickup.consignee.name,
    address: pickup.consignee.address,
    phone: pickupRequest.consigneePhone || "",
    email: pickupRequest.consigneeEmail || "",
  };
}

const toNumber = (value?: string) => ((value && parseFloat(value)) || undefined);

export const mapToPickupItemsModel = (pickup: PickupsResponseContentModel): ItemModel[] => {
  return pickup.itemsList.map(item => ({
    ...item,
    length: toNumber(item.length),
    width: toNumber(item.width),
    diameter: toNumber(item.diameter),
    height: toNumber(item.height),
  }));
}